import {
	EDIT_PAGE_FEATURES,
	DEFAULT_ALLOWED_FEATURES,
} from '@atlassian/embedded-confluence/features';

import { useAllowedFeatures } from '../common/useAllowedFeatures.ts';
import type { FeatureMode } from '../common/useAllowedFeatures.ts';

const { edit: EDIT_PAGE_DEFAULT_ALLOWED_FEATURES } = DEFAULT_ALLOWED_FEATURES;

export enum EmbeddedEditPageButtons {
	DELETE_DRAFT = 'delete-draft',
	TEMPLATE_BROWSER = 'template-browser',
}

type TemplateBrowserConfig = { 'template-browser': { 'space-picker-disabled'?: boolean } };
type LoomExternalOpenConfig = { 'loom-external-open': { route: string } };

export const useEmbeddedEditPageFeatures = () => {
	const allowedFeatures =
		useAllowedFeatures<FeatureMode.EDIT>() || EDIT_PAGE_DEFAULT_ALLOWED_FEATURES;

	/**
	 * When allowedFeatures === all, we don't want to enable features that are suppose to disable features.
	 * For example, we want to keep "disable-publish-close-buttons" out of allowedEditPageFeatures,
	 * so that the Publish and Close buttons won't be hidden when allowedFeatures is "all".
	 */
	const disableFeatures = ['disable-publish-close-buttons'];

	const allAllowedFeaturesEnabled = allowedFeatures === 'all';

	const allowedEditPageFeatures = allAllowedFeaturesEnabled
		? EDIT_PAGE_FEATURES.filter((feature) => !disableFeatures.includes(feature))
		: allowedFeatures;

	const allowedEllipsisButtons: EmbeddedEditPageButtons[] = [];

	const isDeleteDraftEnabled = allowedEditPageFeatures?.includes(
		EmbeddedEditPageButtons.DELETE_DRAFT,
	);

	if (isDeleteDraftEnabled) {
		allowedEllipsisButtons.push(EmbeddedEditPageButtons.DELETE_DRAFT);
	}

	const templateBrowserConfig: TemplateBrowserConfig | undefined = !allAllowedFeaturesEnabled
		? (allowedFeatures as Array<unknown>)?.find(
				(x): x is TemplateBrowserConfig =>
					typeof x === 'object' && x !== null && 'template-browser' in x,
			)
		: undefined;

	const isTemplateBrowserEnabled =
		allowedEditPageFeatures?.includes(EmbeddedEditPageButtons.TEMPLATE_BROWSER) ||
		!!templateBrowserConfig;

	if (isTemplateBrowserEnabled) {
		allowedEllipsisButtons.push(EmbeddedEditPageButtons.TEMPLATE_BROWSER);
	}

	const isSpacePickerDisabled =
		templateBrowserConfig?.['template-browser']?.['space-picker-disabled'] ?? false;

	const hasInlineCommentsAndHighlightActions = allowedEditPageFeatures?.includes('inline-comments');

	const isPublishModalEnabled = allowedEditPageFeatures?.includes('publish-modal');

	const hidePublishCloseButtons = allowedEditPageFeatures?.includes(
		'disable-publish-close-buttons',
	);

	const loomExternalOpenRoute: string | undefined = (
		allowedEditPageFeatures as Array<unknown>
	)?.find(
		(x): x is LoomExternalOpenConfig =>
			typeof x === 'object' && x !== null && 'loom-external-open' in x,
	)?.['loom-external-open']?.route;

	return {
		allowedEllipsisButtons,
		isTemplateBrowserEnabled,
		isSpacePickerDisabled,
		hasInlineCommentsAndHighlightActions,
		isPublishModalEnabled,
		hidePublishCloseButtons,
		loomExternalOpenRoute,
	};
};
